import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import { tracking } from '#features/analytics';
import { Button, Chip, Image, Link, Panel, PeopleIcon } from '#features/ui';

type BlogGameActionProps = {
  gameId: string;
  gameName?: string;
  slug: string;
  exampleImage?: string;
};

export const BlogGameAction = ({ gameId, gameName, slug, exampleImage }: BlogGameActionProps) => {
  const { _ } = useLingui();

  return (
    <Panel
      as="aside"
      variant="container"
      size="lg"
      mt={10}
      position="relative"
      overflow="hidden"
      aria-labelledby="blog-teamplay-action-title"
    >
      <Box
        position="absolute"
        boxSize="64"
        top="-24"
        right="-20"
        bg="purple.solid"
        opacity="0.14"
        filter="blur(48px)"
        borderRadius="full"
        pointerEvents="none"
      />
      <PeopleIcon
        width="210"
        height="172"
        color="fg.link"
        aria-hidden="true"
        style={{
          position: 'absolute',
          right: '-34px',
          top: '-24px',
          opacity: 0.08,
          pointerEvents: 'none',
        }}
      />

      <Stack gap={6} position="relative">
        <Flex gap={4} align="flex-start" pr={{ base: 0, md: 24 }}>
          <Panel
            variant="nested"
            size="sm"
            display={{ base: 'none', sm: 'flex' }}
            alignItems="center"
            justifyContent="center"
            color="fg.link"
            flexShrink={0}
          >
            <PeopleIcon width="32" height="26" aria-hidden="true" />
          </Panel>
          <Box>
            <Chip variant="fact" size="sm" emphasis="strong" mb={3}>
              Teamplay
            </Chip>
            <Text id="blog-teamplay-action-title" as="h2" textStyle="article.h2" mb={3}>
              {_(
                msg({
                  id: 'blog.gameAction.title',
                  message: 'Less random. More teamplay.',
                })
              )}
            </Text>
            <Text textStyle="article.body" color="fg.muted">
              {gameName && <>{gameName}: </>}
              {_(
                msg({
                  id: 'blog.gameAction.body',
                  message:
                    'Skip mismatched randoms. Show what kind of run you want and find players who are after the same experience.',
                })
              )}
            </Text>
          </Box>
        </Flex>

        {exampleImage && (
          <Panel variant="object" p={2} overflow="hidden">
            <Image
              src={exampleImage}
              alt={_(
                msg({
                  id: 'blog.gameAction.previewAlt',
                  message: 'Fictional Teamplay social preview example',
                })
              )}
              width={1200}
              height={600}
              style={{ display: 'block', width: '100%', height: 'auto' }}
            />
          </Panel>
        )}

        <Panel
          variant="nested"
          size="md"
          display="flex"
          flexDirection={{ base: 'column', sm: 'row' }}
          alignItems={{ base: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={4}
        >
          <Box>
            <Text textStyle="strong" color="fg.emphasized" mb={1}>
              {_(msg({ id: 'blog.gameCta.title', message: 'Turn the update into a run' }))}
            </Text>
            <Text textStyle="caption" color="fg.muted">
              {_(
                msg({
                  id: 'blog.gameCta.hint',
                  message: 'The game is already selected. Add your goal, platform and language.',
                })
              )}
            </Text>
          </Box>
          <Button
            minH="44px"
            h="auto"
            py={3}
            whiteSpace="normal"
            textAlign="center"
            asChild
            flexShrink={0}
            onClick={() =>
              tracking.event({
                action: 'blog_create_post_click',
                category: 'blog',
                label: slug,
              })
            }
          >
            <Link to="/{-$locale}/dashboard/discover/create" search={{ gameId, blog: slug }}>
              {_(msg({ id: 'blog.gameCta.create', message: 'Create a teammate post' }))}
            </Link>
          </Button>
        </Panel>
      </Stack>
    </Panel>
  );
};
